import { CSSObject } from '@emotion/react';
import { useFlagMe139059FixMinimumWidthPhoneinputComponent } from '@generated/flags/ME-139059-fix-minimum-width-phoneinput-component';
import { FC, useEffect, useRef } from 'react';
import { PhoneInputProps } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { PhoneValue, usePhoneLib } from '../PhoneValue';
import { CountrySelect } from './CountrySelect';

export interface Props extends Omit<PhoneInputProps, 'value' | 'country'> {
  value?: Maybe<string>;
  /** Defaults to US */
  hideCountryPicker?: boolean;
}

const CountryPickerNoop = (): null => null;

export const PhoneInput: FC<Props> = (props) => {
  const enablePhoneInputMinimumWidth =
    useFlagMe139059FixMinimumWidthPhoneinputComponent();
  const phoneLib = usePhoneLib();
  const { hideCountryPicker, defaultCountry = 'US' } = props;

  const coercedValue = props.value || '';

  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (phoneLib && !hasNotifiedRef.current && props.defaultCountry !== 'US') {
      const countryCode = phoneLib.getCountryCallingCode(defaultCountry);
      let modifiedCoercedValue = coercedValue;
      if (coercedValue && !coercedValue.startsWith('+') && countryCode) {
        modifiedCoercedValue = `+${countryCode} ${modifiedCoercedValue}`;
      }
      props.onChange(modifiedCoercedValue);
      hasNotifiedRef.current = true;
    }
  }, [coercedValue, defaultCountry, phoneLib, props]);

  if (!phoneLib) {
    return <input disabled />;
  }

  const Component = phoneLib.default;

  if (props.readOnly) {
    return (
      <div data-readonly>
        <PhoneValue value={props.value} />
      </div>
    );
  }

  const hasNonUSCoercedCountry =
    (props.value || '').startsWith('+') &&
    !(props.value || '').startsWith('+1');

  const extraProps: anyOk = {
    initialValueFormat: hideCountryPicker ? undefined : 'national',
  };

  if (hasNonUSCoercedCountry) {
    extraProps.initialValueFormat = 'international';
  }

  const phoneInputWidthStyle: CSSObject = {
    'input[type="tel"]': {
      minWidth: '115px',
    },
  };
  return (
    <div
      css={{
        '.PhoneInput': {
          display: 'grid',
          gridTemplateColumns: hideCountryPicker ? '1fr' : '36px 1fr',
          gap: 2,
        },
      }}
    >
      <Component
        defaultCountry="US"
        {...extraProps}
        countrySelectComponent={
          hideCountryPicker ? CountryPickerNoop : CountrySelect
        }
        withCountryCallingCode={hideCountryPicker}
        international={hideCountryPicker}
        {...props}
        useNationalFormatForDefaultCountryValue
        value={coercedValue}
        css={enablePhoneInputMinimumWidth && { ...phoneInputWidthStyle }}
      />
    </div>
  );
};
